import {
	Stack,
	Text,
	Button,
	Card,
	Group,
	PasswordInput,
	Flex,
	Anchor,
	Box
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useSecuritySectionStore } from "../stores/securitySectionStore"
import { useSecurityMutations } from "../hooks/useSecurityMutations"
import { PasswordStrength } from "@components/ui/PasswordStrength/PasswordStrength"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { useEffect, useState } from "react"
import { getPasswordValidationDetails } from "@utils/password.utils"
import { useAuthContext } from "@context/AuthProvider"
import { eventTrackingItems } from "@utils/eventTracking"

export const PasswordChange = () => {
	const { goToIdle, updateUser } = useSecuritySectionStore()
	const { refreshUserProfile } = useAuthContext()
	const { changePassword } = useSecurityMutations()
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const passwordMismatchErrorMessage =
		dictionary?.passwordMatchErrorMessage ?? ""
	const [hasErrors, setHasErrors] = useState(false)
	const [hasFocusedConfirmPassword, setHasFocusedConfirmPassword] =
		useState(false)
	const form = useForm({
		validateInputOnChange: true,
		initialValues: {
			currentPassword: "",
			newPassword: "",
			confirmPassword: ""
		},
		validate: {
			currentPassword: (value: string) =>
				!value ? dictionary?.currentPasswordRequired : null,
			newPassword: (value: string) => {
				if (!value) return dictionary?.newPasswordRequired

				const validation = getPasswordValidationDetails(value)

				if (validation.hasInvalidSpecialChar)
					return dictionary?.passwordDisallowedCharError
				if (!validation.hasValidLength) return dictionary?.passwordLengthRule
				if (!validation.hasUpperCase) return dictionary?.passwordUppercaseRule
				if (!validation.hasLowerCase) return dictionary?.passwordLowercaseRule
				if (!validation.hasNumber) return dictionary?.passwordNumberRule
				if (!validation.hasSpecialChar)
					return dictionary?.passwordSpecialCharRule

				return null
			},
			confirmPassword: (value: string, values: { newPassword: string }) => {
				if (!value) return null
				return value !== values.newPassword
					? passwordMismatchErrorMessage
					: null
			}
		}
	})

	useEffect(() => {
		form.validate()
		const np = form.values.newPassword ?? ""
		const vnp = getPasswordValidationDetails(np)
		const newPasswordInvalid =
			!np ||
			vnp.hasInvalidSpecialChar ||
			!vnp.hasValidLength ||
			!vnp.hasUpperCase ||
			!vnp.hasLowerCase ||
			!vnp.hasNumber ||
			!vnp.hasSpecialChar

		setHasErrors(newPasswordInvalid)
	}, [
		form.values.currentPassword,
		form.values.newPassword,
		form.values.confirmPassword
	])

	const onUpdateEvent = (label?: string, type?: string) => {
		eventTrackingItems({
			event_name: "click",
			link_url: "",
			category: "click",
			interactionType: label ?? "",
			navLinkText: type ?? ""
		})
	}

	const handleCancel = () => {
		goToIdle()
		onUpdateEvent(dictionary?.cancelButtonLabel, "Cancel click")
	}

	const passwordsMatch =
		form.values.newPassword === form.values.confirmPassword &&
		form.values.newPassword.length > 0
	const showConfirmPasswordMismatch =
		hasFocusedConfirmPassword &&
		form.values.confirmPassword.length > 0 &&
		form.values.confirmPassword !== form.values.newPassword

	const handleSubmit = form.onSubmit(async (values) => {
		setErrorMessage(null)
		changePassword.mutate(
			{
				currentPassword: values.currentPassword,
				newPassword: values.newPassword
			},
			{
				onSuccess: async () => {
					try {
						const updatedProfile = await refreshUserProfile()
						const lastPasswordUpdate =
							updatedProfile.lastPasswordChangeDate || new Date().toISOString()
						updateUser({ lastPasswordUpdate })
						goToIdle()
						onUpdateEvent(
							dictionary?.updatePasswordLabel,
							"Update Password click"
						)
					} catch (error) {
						console.error(
							"Error refreshing user profile after password change:",
							error
						)
						updateUser({
							lastPasswordUpdate: new Date().toISOString()
						})
						goToIdle()
					}
				},
				onError: (error) => {
					setErrorMessage(
						error.response?.data?.message?.message || "Error changing password"
					)
				}
			}
		)
	})

	return (
		<Card p={40} radius="sm" shadow="sm" maw={920}>
			<Group justify="space-between" align="center" mb="xl">
				<Text fw={600} size="xl">
					{dictionary?.editPasswordLabel}
				</Text>
				<Anchor
					underline="always"
					fw={600}
					onClick={handleCancel}
					component="button"
				>
					{dictionary?.cancelButtonLabel}
				</Anchor>
			</Group>

			<Text size="sm" c="dimmed" mb="xl">
				{dictionary?.editPasswordDescription}
			</Text>

			<form onSubmit={handleSubmit}>
				<Stack gap="xl">
					<Stack gap="xs">
						<PasswordInput
							label={dictionary?.currentPasswordLabel}
							{...form.getInputProps("currentPassword")}
							disabled={changePassword.isPending}
							size="md"
						/>
					</Stack>

					<Flex gap="xl" w="100%" direction={{ base: "column", md: "row" }}>
						<Box flex={1} mih={86}>
							<PasswordInput
								label={dictionary?.newPasswordLabel}
								{...form.getInputProps("newPassword")}
								disabled={changePassword.isPending}
								size="md"
							/>
						</Box>
						<Box flex={1} mih={86}>
							<PasswordInput
								label={dictionary?.confirmNewPasswordLabel}
								{...form.getInputProps("confirmPassword")}
								error={
									showConfirmPasswordMismatch
										? passwordMismatchErrorMessage
										: null
								}
								onFocusCapture={() => setHasFocusedConfirmPassword(true)}
								disabled={changePassword.isPending}
								size="md"
							/>
						</Box>
					</Flex>

					{errorMessage && (
						<Text c="red" size="sm" fw={500}>
							{errorMessage}
						</Text>
					)}

					{form.values.newPassword && (
						<Group justify="center" w="100%">
							<PasswordStrength
								password={form.values.newPassword}
								confirmPassword={form.values.confirmPassword}
								showRequirements={true}
							/>
						</Group>
					)}

					<Group justify="center" mt="xl">
						<Button
							type="submit"
							variant="dark-blue"
							size="lg"
							loading={changePassword.isPending}
							disabled={hasErrors || !passwordsMatch}
							w={{ base: "100%", sm: "auto" }}
							maw={{ base: "100%", sm: "none" }}
							styles={{
								label: {
									whiteSpace: "normal",
									wordBreak: "break-word",
									overflowWrap: "break-word"
								}
							}}
						>
							{dictionary?.updatePasswordLabel}
						</Button>
					</Group>
				</Stack>
			</form>
		</Card>
	)
}
